import { useRef, useCallback, useState, useEffect } from 'react';
import { useLockScroll } from '@/hooks';
import Mask from './mask';
import classnames from 'classnames';

import styles from './modal.module.scss';

import type { FC, CSSProperties, ReactNode } from 'react';

export type Placement = 'top' | 'right' | 'bottom' | 'left' | 'center';

export interface ModalProps {

  /**
   * @description  容器类名	
   */
  className?: string;

  /**
    * @description 对话框外层容器的类名	
    */
  wrapClassName?: string;

  /**
  * @description 可用于设置浮层的样式，调整浮层位置等
  */
  style?: CSSProperties;

  /**
 * @description 子元素
 */
  children?: ReactNode;
  /**
  * @description 对话框是否可见	
  */
  isOpen?: boolean;

  /**
  * @description 是否展示遮罩
  */
  mask?: boolean;

  /**
  * @description 遮罩样式
  */
  maskStyle?: CSSProperties;

  /**
  * @description 点击遮罩层或右上角叉或取消按钮的回调
  */
  onCancel?: (e?: React.SyntheticEvent) => void;

  /**
 * @description 弹窗完全关闭回调
 */
  afterClose?: () => void;

  /**
 * @description 点击蒙层是否允许关闭	
 * @default false	
 */
  maskClosable?: boolean;

  /**
* @description 自定义关闭图标			
*/
  closeIcon?: ReactNode;

  /**
* @description 是否显示右上角的关闭按钮		
* @default true	
*/
  closable?: boolean;

  /**
  * @description 	top | right | bottom | left | center
  * @default center
  */
  placement?: Placement

  /**
  * @description 	是否开启响应式布局
  * @default { mobile: 'center', pc: 'center' },
  */
  responsive?: boolean | { mobile?: Placement, pc?: Placement};

   /**
  * @description 	宽度	
  * @default 500
  */
  width?: string | number;

} 

const Modal: FC<ModalProps> = (props) => {

  const {
    className,
    wrapClassName,
    style,
    isOpen,
    children,
    onCancel,
    afterClose,
    mask = true,
    maskStyle,
    maskClosable = false,
    closeIcon,
    closable = true,
    width
  } = props;

  const prefixCls = 'web_components_modal';
  const modalRef = useRef<HTMLDivElement>(null);
  useLockScroll(modalRef, isOpen ? 'strict' : false);

  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setOpenModal(isOpen);
    } 
  }, [isOpen])

  const onWrapperClick = (e: React.SyntheticEvent) => {
    if (maskClosable && (e.target === modalRef.current)) {
      onCancel?.(e);
    }
  }

  const handleAnimationEnd = useCallback(() => {
    if (!isOpen) {
      setOpenModal(false);
      afterClose && afterClose();
    }
  }, [isOpen])

  const renderClose = () => {

    if (closable) {
      return (
        <div className={`${prefixCls}_close`} onClick={onCancel}>
          {
            closeIcon || <i className='iconfont icon-close' />
          }
        </div>
      )
    }

    return null;

  }

  return (
    <div
      style={style}
      className={classnames(styles[`${prefixCls}_root`], `${prefixCls}_root`, className)}>
      {
        mask && <Mask maskStyle={maskStyle} isOpen={isOpen} onClick={maskClosable ? onCancel : undefined} />
      }
      <div
        className={
          classnames(
            `${prefixCls}_wrap`,
            {
              [`${prefixCls}_show`]: isOpen,
              [`${prefixCls}_hidden`]: !isOpen,
              [styles[`${prefixCls}_show`]]: isOpen,
              [styles[`${prefixCls}_hidden`]]: !isOpen
            },
            wrapClassName,
          )
        }
        style={!openModal ? { display: 'none' } : undefined}
        onAnimationEnd={handleAnimationEnd}
      >
        <div ref={modalRef}
          onClick={onWrapperClick} className={prefixCls}>
          <div className={`${prefixCls}_content`} style={{ width }}>
            {renderClose()}
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Modal;